import {faGear} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Col, Modal, Row, Space} from 'antd';
import {useEffect, useMemo, useState} from 'react';

import {PageCard} from '../../../../../layout/page-card/page-card';
import {Locale} from '../../../../../provider/locale/locale';
import {useAiMonitoringScores} from '../../../../../service/ai-brand-monitoring/ai-brand-monitoring-hook';
import {
    AiMonitoringConfigType,
    AiMonitoringRunType,
} from '../../../../../service/ai-brand-monitoring/ai-brand-monitoring-type';
import {RunNowButton} from '../run/run-now-button';
import {SettingsForm} from '../settings/settings-form';

import {RunSection} from './run-section';

type PropsType = {
    brandId: number;
    config: AiMonitoringConfigType;
    run: AiMonitoringRunType | undefined;
    currentRunId: string | null;
    onRunFinished: (runId: string) => void;
};

export function DashboardView(props: PropsType): JSX.Element {
    const {brandId, config, run, currentRunId, onRunFinished} = props;

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const {data: scores} = useAiMonitoringScores(brandId, {granularity: 'day'});

    // Default to the latest finished run (last point of the score series) so the most
    // recent results show on load without having to start a run first.
    useEffect(() => {
        if (currentRunId || !scores?.series.length) {
            return;
        }

        const latest = scores.series[scores.series.length - 1];

        if (latest) {
            onRunFinished(latest.runId);
        }
    }, [currentRunId, scores, onRunFinished]);

    const canManage = config.permissions.canManage;

    // Current config competitors (by stable ref id) — the dynamics chart shows only these.
    const competitorIds = useMemo<Array<number>>(
        () =>
            config.competitors
                .filter((competitor) => competitor.isActive && competitor.competitorRefId !== null)
                .map((competitor) => competitor.competitorRefId as number),
        [config]
    );

    return (
        <>
            <PageCard>
                <Row align="middle" gutter={[16, 16]} justify="end">
                    <Col>
                        <Space>
                            {canManage ? <RunNowButton brandId={brandId} onRunFinished={onRunFinished} /> : null}
                            <Button icon={<FontAwesomeIcon icon={faGear} />} onClick={() => setIsSettingsOpen(true)}>
                                <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__TITLE" />
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </PageCard>

            <RunSection
                brandId={brandId}
                competitorIds={competitorIds}
                currentRunId={currentRunId}
                run={run}
                series={scores?.series ?? []}
            />

            <Modal
                destroyOnClose
                footer={null}
                onCancel={() => setIsSettingsOpen(false)}
                open={isSettingsOpen}
                title={<Locale stringKey="AI_BRAND_MONITORING__SETTINGS__TITLE" />}
                width={720}
            >
                <SettingsForm brandId={brandId} config={config} onSaved={() => setIsSettingsOpen(false)} />
            </Modal>
        </>
    );
}
